/**
 * Admin Providers API Service
 * HTTP client methods for provider management endpoints
 */

import { apiClient } from '@/core/api/client';
import type { PaginatedResponse } from '../types/pagination.type';
import type {
  AdminProvider,
  CreateProviderPayload,
  UpdateProviderPayload,
  ProviderListParams,
} from '../types/admin-provider.type';

const BASE_PATH = '/admin/providers';

/**
 * List providers with pagination and filters
 * GET /admin/providers
 * 
 * @param params - Query parameters (page, pageSize, search, filters)
 * @returns Paginated list of providers with total count reflecting filters
 */
export async function listProviders(
  params?: ProviderListParams,
): Promise<PaginatedResponse<AdminProvider>> {
  const response = await apiClient.get<PaginatedResponse<AdminProvider>>(
    BASE_PATH,
    { params },
  );
  return response.data;
}

/**
 * Get single provider by ID
 * GET /admin/providers/:id
 * 
 * @param id - Provider ID
 * @returns Provider details
 */
export async function getProvider(id: string): Promise<AdminProvider> {
  const response = await apiClient.get<AdminProvider>(`${BASE_PATH}/${id}`);
  return response.data;
}

/**
 * Get full provider data for editing (includes all relationships and user data)
 * GET /admin/providers/:id/full
 * 
 * @param id - Provider ID
 * @returns Complete provider data with user, categories, specialties, expertise, etc.
 */
export async function getProviderFull(id: string): Promise<AdminProvider> {
  const response = await apiClient.get<AdminProvider>(`${BASE_PATH}/${id}/full`);
  return response.data;
}

/**
 * Create new provider with user account
 * POST /admin/providers
 * 
 * @param payload - Complete provider data including user info and optional user.partnerCompanyId
 * @returns Created provider with relationships
 */
export async function createProvider(
  payload: CreateProviderPayload,
): Promise<AdminProvider> {
  const response = await apiClient.post<AdminProvider>(BASE_PATH, payload);
  return response.data;
}

/**
 * Update existing provider
 * PATCH /admin/providers/:id
 * 
 * @param id - Provider ID
 * @param payload - Partial provider data to update
 * @returns Updated provider
 */
export async function updateProvider(
  id: string,
  payload: UpdateProviderPayload,
): Promise<AdminProvider> {
  const response = await apiClient.patch<AdminProvider>(
    `${BASE_PATH}/${id}`,
    payload,
  );
  return response.data;
}

/**
 * Soft-delete provider
 * DELETE /admin/providers/:id
 * 
 * @param id - Provider ID
 * @returns void (204 No Content)
 */
export async function softDeleteProvider(id: string): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/${id}`);
}

/**
 * Get count of pending providers (for badge)
 * Derived from listProviders with status filter
 * 
 * @returns Count of providers with PENDING status
 */
export async function getPendingProvidersCount(): Promise<number> {
  const response = await listProviders({ 
    status: 'pending', 
    pageSize: 1, // We only need the count
    page: 1 
  });
  return response.meta.total;
}

/**
 * Update provider email (syncs with Kinde identity provider)
 * PATCH /admin/providers/:id/email
 *
 * @param id - Provider ID
 * @param email - New email address
 */
export async function updateProviderEmail(
  id: string,
  email: string,
): Promise<void> {
  await apiClient.patch(`${BASE_PATH}/${id}/email`, { email });
}

/**
 * Default export for convenient importing
 */
export const adminProvidersApi = {
  listProviders,
  getProvider,
  getProviderFull,
  createProvider,
  updateProvider,
  updateProviderEmail,
  softDeleteProvider,
  getPendingProvidersCount,
};
